import { type FC, useCallback, useMemo, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarFile, F0Button, F0Card, F0Dialog, F0TagStatus } from '@factorialco/f0-react'
import { Dropdown, ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Ellipsis, EyeVisible, FileSigned } from '@factorialco/f0-react/icons/app'
import {
  EmployeeDetailsDocument,
  ItManagementValueObjectsItAssetDocumentTypeEnum,
  SendEmployeeDocumentTypeForSignatureItAssetDocumentDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DocumentModal from 'modules/documents/components/DocumentModal'
import { safeAssetTypeAsIcon } from 'modules/it_management/components/AssetsList/utils'
import { useGroupedDocumentsByEmployee } from 'modules/it_management/hooks/useGroupedDocumentsByEmployee'
import { useItAssetDocumentUpdated } from 'modules/it_management/hooks/useItAssetDocumentUpdated'

type CompanyEmployee = {
  id: string
  firstName: string
  lastName: string
  employeeName: string
  email?: string
  isActive: boolean
  startDate: string | null
  workplace: string
  totalItAssets: number
  hasDraftDocuments: boolean
  hasPendingSignatureDocuments: boolean
  access?: {
    id: string
    avatar?: string | null
  } | null
}

interface Props {
  employee: CompanyEmployee
  open: boolean
  onClose: () => void
}

const EmployeeDetailsSidePanel: FC<Props> = ({ employee, open, onClose }) => {
  const { company, access } = useCurrentSession()
  const { navigation } = useNavigation()
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)

  const { data, loading } = useQuery(EmployeeDetailsDocument, {
    variables: { employeeId: employee.id },
    skip: !employee.id || !open,
  })

  useItAssetDocumentUpdated({
    employeeId: employee.id,
  })

  const [sendEmployeeDocumentTypeForSignature] = useMutation(
    SendEmployeeDocumentTypeForSignatureItAssetDocumentDocument
  )

  const assets = data?.itManagement?.itAssetsConnection?.nodes || []

  const rawDocuments = useMemo(
    () => data?.itManagement?.itAssetDocumentsConnection?.edges?.map((edge) => edge.node) || [],
    [data?.itManagement?.itAssetDocumentsConnection?.edges]
  )

  const documents = useGroupedDocumentsByEmployee(rawDocuments)

  const getSignatureStatus = (
    signatureStatus: string | null | undefined
  ): { text: string; variant: 'neutral' | 'info' | 'positive' | 'warning' | 'critical' } => {
    switch (signatureStatus) {
      case 'draft':
        return {
          text: i18n.t('it_management.people_tab.employee_details.draft'),
          variant: 'neutral',
        }
      case 'pending':
        return {
          text: i18n.t('it_management.people_tab.employee_details.pending'),
          variant: 'warning',
        }
      case 'partially_signed':
        return {
          text: i18n.t('it_management.people_tab.employee_details.partially_signed'),
          variant: 'info',
        }
      case 'completed':
        return {
          text: i18n.t('it_management.people_tab.employee_details.completed'),
          variant: 'positive',
        }
      case 'declined':
        return {
          text: i18n.t('it_management.people_tab.employee_details.declined'),
          variant: 'critical',
        }
      case 'bounced_email':
        return {
          text: i18n.t('it_management.people_tab.employee_details.bounced_email'),
          variant: 'critical',
        }
      case 'cancelled':
        return {
          text: i18n.t('it_management.people_tab.employee_details.cancelled'),
          variant: 'neutral',
        }
      case 'error':
        return {
          text: i18n.t('it_management.people_tab.employee_details.error'),
          variant: 'critical',
        }
      case 'expired':
        return {
          text: i18n.t('it_management.people_tab.employee_details.expired'),
          variant: 'critical',
        }
      default:
        return {
          text: i18n.t('it_management.people_tab.employee_details.draft'),
          variant: 'neutral',
        }
    }
  }

  const fullName = employee.employeeName || `${employee.firstName} ${employee.lastName}`
  const avatarUrl = employee.access?.avatar ? buildUrl(employee.access.avatar) : ''

  const isActive = employee.isActive

  const metadata: Array<{ label: string; value: { type: 'text'; content: string } }> = [
    employee.startDate && {
      label: i18n.t('it_management.people_tab.employee_details.start_date'),
      value: {
        type: 'text' as const,
        content: dateBackendDateTimeToUI(employee.startDate),
      },
    },
  ].filter((item): item is { label: string; value: { type: 'text'; content: string } } =>
    Boolean(item)
  )

  const handleClose = useCallback(() => {
    if (selectedDocumentId) {
      setSelectedDocumentId(null)
    } else {
      onClose()
    }
  }, [selectedDocumentId, onClose])

  return (
    <>
      <F0Dialog
        isOpen={open}
        onClose={handleClose}
        position="right"
        module={{
          id: 'it_management',
          label: i18n.t('it_management.it_inventory.title'),
          href: '/it-inventory',
        }}
      >
        {selectedDocumentId && (
          <DocumentModal
            documentId={selectedDocumentId}
            documents={[]}
            onClose={() => setSelectedDocumentId(null)}
          />
        )}

        <ResourceHeader
          title={fullName}
          description={employee.email || ''}
          avatar={{
            type: 'person',
            firstName: employee.firstName,
            lastName: employee.lastName,
            src: avatarUrl || undefined,
          }}
          metadata={metadata}
          status={{
            label: i18n.t('it_management.people_tab.employee_details.status'),
            text: isActive
              ? i18n.t('it_management.people_tab.employee_details.active_employee')
              : i18n.t('it_management.people_tab.employee_details.inactive'),
            variant: isActive ? 'positive' : 'neutral',
          }}
        />

        <Box gap="s32" paddingTop="s32">
          {/* Devices Section */}
          <Box gap="s16">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('it_management.people_tab.employee_details.devices_assigned')}
            </Text>
            {loading ? (
              <Text.Normal color="grey600">{i18n.t('common.loading')}</Text.Normal>
            ) : assets.length > 0 ? (
              <Box gap="s12">
                {assets.map((asset) => (
                  <F0Card
                    key={asset.id}
                    compact
                    avatar={{
                      type: 'icon',
                      icon: safeAssetTypeAsIcon(asset.itAssetModel?.typeName ?? ''),
                    }}
                    title={`${asset.itAssetModel?.brand} ${asset.itAssetModel?.name}`}
                    onClick={() => {
                      const url = navigation.it_management.assetId.fullPath({ assetId: asset.id })
                      window.open(url, '_blank', 'noopener,noreferrer')
                    }}
                  />
                ))}
              </Box>
            ) : (
              <></>
            )}
          </Box>

          {/* Documents Section */}
          <Box gap="s16">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('it_management.people_tab.employee_details.it_documents')}
            </Text>
            {loading ? (
              <Text.Normal color="grey600">{i18n.t('common.loading')}</Text.Normal>
            ) : documents.length > 0 ? (
              <Box
                borderRadius={{ all: 'abs008' }}
                border={{ all: { color: 'grey300', width: 's1', style: 'solid' } }}
              >
                {documents.map((document, index) => {
                  const signatureStatus = getSignatureStatus(document.signatureStatus)
                  const isDraft = document.signatureStatus === 'draft'

                  const dropdownItems = []

                  if (isDraft) {
                    dropdownItems.push({
                      label: i18n.t('it_management.people.actions.send_for_signature'),
                      icon: FileSigned,
                      onClick: async () => {
                        track(
                          trackingEvents.itManagement.itAssetDocument.sendDocumentForSignature,
                          {
                            company_id: company.id,
                            user_id: access.id,
                            origin: 'inside_people_view',
                          }
                        )

                        try {
                          // @ts-expect-error - Backend validates documentType is 'assign' or 'unassign'
                          const documentType: ItManagementValueObjectsItAssetDocumentTypeEnum =
                            document.documentType

                          await sendEmployeeDocumentTypeForSignature({
                            variables: {
                              companyId: company.id,
                              documentType,
                              employeeId: employee.id,
                            },
                          })

                          app.setFlash({
                            type: 'correct',
                            message: i18n.t('it_management.people.send_signature.success'),
                          })
                        } catch (error) {
                          app.setFlash({
                            type: 'wrong',
                            message: i18n.t('it_management.people.send_signature.error'),
                          })
                        }
                      },
                    })
                  }

                  if (document.documentId) {
                    dropdownItems.push({
                      label: i18n.t('it_management.asset_details.documents.view_document'),
                      icon: EyeVisible,
                      onClick: () => {
                        setSelectedDocumentId(String(document.documentId))

                        track(trackingEvents.itManagement.itAssetDocument.viewItDocuments, {
                          company_id: company.id,
                          user_id: access.id,
                        })
                      },
                    })
                  }

                  const documentTypeLabel =
                    document.documentType === 'assign'
                      ? i18n.t('it_management.people_tab.employee_details.assign')
                      : i18n.t('it_management.people_tab.employee_details.unassign')

                  const documentDisplayName =
                    document.documentFilename || 'Document (' + documentTypeLabel + ')'

                  const isLast = index === documents.length - 1
                  return (
                    <Box key={document.id}>
                      <Box padding="s16" flexDirection="row" alignItems="center" gap="s12">
                        {/* File icon and name - takes remaining space */}
                        <Box
                          flexDirection="row"
                          alignItems="center"
                          gap="s12"
                          style={{ flex: '1', minWidth: '0', overflow: 'hidden' }}
                        >
                          <Box flexNoShrink>
                            <F0AvatarFile
                              file={{
                                name: documentDisplayName,
                                type: 'application/pdf',
                              }}
                              size="md"
                            />
                          </Box>
                          <Box
                            style={{
                              minWidth: '0',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            <Text size="200">{documentDisplayName}</Text>
                          </Box>
                        </Box>

                        {/* Status - fixed position */}
                        <Box flexNoShrink>
                          <F0TagStatus
                            text={signatureStatus.text}
                            variant={signatureStatus.variant}
                          />
                        </Box>

                        {/* Actions Menu - fixed position */}
                        <Box flexNoShrink style={{ width: '40px' }}>
                          {dropdownItems.length > 0 && (
                            <Dropdown items={dropdownItems}>
                              <F0Button variant="tertiary" size="md" icon={Ellipsis} label="" />
                            </Dropdown>
                          )}
                        </Box>
                      </Box>
                      {/* Separator line between items */}
                      {!isLast && (
                        <>
                          <Box height="s1" background="grey300" />
                        </>
                      )}
                    </Box>
                  )
                })}
              </Box>
            ) : (
              <Text.Normal color="grey600">
                {i18n.t('it_management.people_tab.employee_details.no_documents')}
              </Text.Normal>
            )}
          </Box>
        </Box>
      </F0Dialog>
    </>
  )
}

export default EmployeeDetailsSidePanel
